import moment from 'moment-timezone';
import { AnswerFormatStyle, AnswerFormat, ValidationFields } from '../types/Survey';
import { CustomFieldDataType, CustomFieldDefinitionModel } from '../types/Project';

type ValidatableField = Pick<CustomFieldDefinitionModel, "dataType">;

type AnswerFormatType = AnswerFormat["type"] | "WebViewStep";

const fieldTypeRestrictions: Partial<Record<CustomFieldDataType, { type: AnswerFormatType, style?: AnswerFormatStyle | "" }[]>> = {
    Integer: [{ type: "WebViewStep", style: "" }, { type: "NumericAnswerFormat", style: "Integer" }, { type: "ScaleAnswerFormat", style: "" }],
    Decimal: [{ type: "WebViewStep", style: "" }, { type: "NumericAnswerFormat", style: "Decimal" }, { type: "ContinuousScaleAnswerFormat", style: "" }, { type: "HeightAnswerFormat" }, { type: "WeightAnswerFormat" }],
    Date: [{ type: "WebViewStep", style: "" }, { type: "DateAnswerFormat", style: "Date" }, { type: "DateOfBirthAnswerFormat" }],
    Timestamp: [{ type: "WebViewStep", style: "" }, { type: "DateAnswerFormat", style: "DateAndTime" }],
    Email: [{ type: "WebViewStep", style: "" }, { type: "TextAnswerFormat" }, { type: "EmailAnswerFormat" }],
    Phone: [{ type: "WebViewStep", style: "" }, { type: "PhoneAnswerFormat" }],
    TimeOfDay: [{ type: "WebViewStep", style: "" }, { type: "TimeOfDayAnswerFormat" }]
};

const allowedDateFormats = [
    "YYYY-MM-DD", // 2017-10-1
    "YYYY-M-D", // 2017-10-1
    "YYYY-MM-D", // 2017-01-1
    "YYYY-M-DD", // 2017-1-01
    "YYYY/MM/DD", // 2017/01/15
    "YYYY/M/D", // 2017/10/1
    "YYYY MM DD", // 2017 01 15
    "YYYY M D", // 2017 10 1
    "YYYY.MM.DD", // 2017.01.15
    "YYYY.M.D", // 2017.10.1
    "MM-DD-YYYY", // 01-15-2017
    "M-D-YYYY", // 10-1-2017
    "MM/DD/YYYY", // 01/15/2017
    "M/D/YYYY", // 10/1/2017
    "MM DD YYYY", // 01 15 2017
    "M D YYYY", // 10 1 2017
    "MM.DD.YYYY", // 01.15.2017
    "M.D.YYYY", // 10.1.2017
    "MMM D YYYY", // Jan 15 2017, Oct 1 2017
    "D MMM YYYY", // 15 Jan 2017, 1 Oct 2017
    "MMMM D YYYY", // January 15 2017, October 1 2017
    "D MMMM YYYY", // 15 January 2017, 1 October 2017
    "YYYY-MM-DD[T]HH:mm:ss" // 2017-01-15T00:00:00Z
];

const allowedTimestampFormats = [
    "YYYY-MM-DD[T]HH:mm:ssZ" // 2017-01-15T13:33:22Z
];

export const emailRegExString = '^(([^<>()\\[\\]\\\\.,;:\\s@\\"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@\\"]+)*)|(\\".+\\"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$';
export const phoneRegExString = '^\\d{10}$';
export const postalRegExString = '^\\d{5}';
export const defaultValidationFields: ValidationFields = {
    validationPattern: undefined,
    keyboardType: "Text",
    invalidMessage: undefined
};

export function isCompatibleAnswerFormat(validatableField: ValidatableField, answerFormat: AnswerFormat) {
    if (!validatableField.dataType) {
        return true;
    }
    var compatibleTemplates = fieldTypeRestrictions[validatableField.dataType];
    if (!compatibleTemplates || compatibleTemplates.length === 0) {
        return true;
    }
    var match = compatibleTemplates.find(template =>
        Object.keys(answerFormat).every((key) => {
            const templateValue = template[key as keyof typeof template];
            const answerFormatValue = answerFormat[key as keyof AnswerFormat];
            if (templateValue == undefined) { return true; }
            if (!templateValue && !answerFormatValue) { return true; }
            return templateValue === answerFormatValue;
        }
        )
    );
    return match;
}

export function validateValue(name: string, dataType: CustomFieldDataType, value: string | number) {
    var validator = fieldValidators[dataType];
    if (value && validator && !validator(value)) {
        return `'${name}' value '${value}' is not valid for field type of ${dataType}`;
    }
}

export function validateValues(definitions: CustomFieldDefinitionModel[], values: Record<string, string | number>) {
    for (var k = 0; k < definitions.length; k++) {
        var fieldDef = definitions[k];
        var fieldValue = values[fieldDef.name];
        if (fieldValue === undefined) {
            // ensure empty date and timestamp fields get persisted; they will be ignored during serialization otherwise.
            values[fieldDef.name] = "";
        }
        var error = validateValue(fieldDef.name, fieldDef.dataType, fieldValue);
        if (error) {
            return error;
        }
    }
    return null;
}

export function getValidationPattern(answerFormat: AnswerFormat, customField?: CustomFieldDefinitionModel): ValidationFields {

    if (answerFormat.demographicField && 'MobilePhone' == answerFormat.demographicField) {
        return {
            validationPattern: phoneRegExString,
            keyboardType: "Phone",
            invalidMessage: 'Please enter a valid phone number'
        }
    }
    else if (answerFormat.demographicField && 'Email' == answerFormat.demographicField) {
        return {
            validationPattern: emailRegExString,
            keyboardType: "Email",
            invalidMessage: 'Please enter a valid email'
        }
    }
    else if (answerFormat.demographicField && 'PostalCode' == answerFormat.demographicField) {
        return {
            validationPattern: postalRegExString,
            keyboardType: "Number",
            invalidMessage: 'Please enter a valid postal code'
        }

    } else if (answerFormat.customField && customField && answerFormat.customField === customField.name) {
        if ('Phone' == customField.dataType) {
            return {
                validationPattern: phoneRegExString,
                keyboardType: "Phone",
                invalidMessage: answerFormat.invalidMessage || 'Please enter a valid phone number'
            }
        } else if ('Email' == customField.dataType) {
            return {
                validationPattern: emailRegExString,
                keyboardType: "Email",
                invalidMessage: answerFormat.invalidMessage || 'Please enter a valid email'
            }
        }
    }

    return defaultValidationFields;
}

const fieldValidators: Partial<Record<CustomFieldDataType, ((value: number | string) => boolean)>> = {
    Integer: function (value: number | string) {
        if (typeof value === "string") {
            const regex = /^-?\d+$/;
            if (!regex.test(value)) {
                return false;
            }
            value = parseInt(value);
        }
        var number = value - 0;
        var isNaN = Number.isNaN(number);
        var isInteger = Number.isSafeInteger(number);
        return !isNaN && isInteger;
    },
    Decimal: function (value: number | string) {
        if (typeof value === "string") {
            const regex = /^-?\d+(\.\d+)?$/;
            if (!regex.test(value)) {
                return false;
            }
            value = parseFloat(value);
        }
        const number = value - 0;
        var isNaN = Number.isNaN(number);
        var isFinite = Number.isFinite(number);
        return !isNaN && isFinite;
    },
    Date: function (value: number | string) {
        if (typeof value === "number") {
            throw new Error("Date field value must be a string");
        }
        var date = moment(value, allowedDateFormats, true);
        return date.isValid();
    },
    Timestamp: function (value: number | string) {
        if (typeof value === "number") {
            throw new Error("Timestamp field value must be a string");
        }
        var timestamp = moment(value, allowedTimestampFormats, true);
        return timestamp.isValid();
    },
    Email: function (value: number | string) {
        if (typeof value === "number") {
            throw new Error("Email field value must be a string");
        }
        var emailRegEx = /^(([^<>()\[\]\\.,;:\s@\"]+(\.[^<>()\[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return emailRegEx.test(value);
    },
    Phone: function (value: number | string) {
        if (typeof value === "number") {
            throw new Error("Phone field value must be a string");
        }
        var phoneRegEx = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
        return phoneRegEx.test(value);
    },
    TimeOfDay: function (value: number | string) {
        if (typeof value === "number") {
            throw new Error("TimeOfDay field value must be a string");
        }
        var date = moment(value, "YYYY-MM-DDTHH:mm:ssZ", true);
        return date.isValid() && date.year() === 2000 && date.month() === 0 && date.date() === 1;
    }
};
